import React from "react";
import { useTranslation } from "react-i18next";

type BoostLevel = 1000 | 5000 | 10000;

interface BoostBadgeProps {
  boostLevel: BoostLevel;
  className?: string;
}

/**
 * BoostBadge - Boost'lu postlar için rozet
 * 
 * Kullanım:
 *   <BoostBadge boostLevel={1000} />
 *   <BoostBadge boostLevel={5000} />
 *   <BoostBadge boostLevel={10000} />
 */
export const BoostBadge: React.FC<BoostBadgeProps> = ({ boostLevel, className = "" }) => {
  const { t } = useTranslation();

  const config = {
    1000: {
      label: t("badge.boost1000"),
      icon: "🔥",
      bg: "linear-gradient(to right, #fb923c, #f59e0b, #facc15)",
      borderColor: "#fdba74",
      glowColor: "0 0 10px rgba(251, 146, 60, 0.5)",
    },
    5000: {
      label: t("badge.boost5000"),
      icon: "✨",
      bg: "linear-gradient(to right, #c084fc, #ec4899, #fb7185)",
      borderColor: "#d8b4fe",
      glowColor: "0 0 10px rgba(192, 132, 252, 0.5)",
    },
    10000: {
      label: t("badge.boost10000"),
      icon: "💎",
      bg: "linear-gradient(to right, #22d3ee, #3b82f6, #6366f1)",
      borderColor: "#67e8f9",
      glowColor: "0 0 10px rgba(34, 211, 238, 0.5)",
    },
  };

  const boostConfig = config[boostLevel];

  return (
    <div
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        padding: "4px 12px",
        borderRadius: "9999px",
        fontSize: "12px",
        fontWeight: "600",
        background: boostConfig.bg,
        color: "#fff",
        border: `2px solid ${boostConfig.borderColor}`,
        boxShadow: `${boostConfig.glowColor}, 0 4px 6px rgba(0,0,0,0.1)`,
      }}
      className={`animate-pulse-slow ${className}`}
      title={boostConfig.label}
    >
      <span style={{ position: "relative", zIndex: 10, fontSize: "14px" }} role="img" aria-label={boostConfig.label}>
        {boostConfig.icon}
      </span>
      <span style={{ position: "relative", zIndex: 10, lineHeight: "1" }}>{boostConfig.label}</span>
    </div>
  );
};

/**
 * BoostFrame - Boost'lu postların kartlarını çevreleyen animasyonlu çerçeve
 * 
 * Kullanım:
 *   <BoostFrame boostLevel={1000}>
 *     <PostCard post={post} />
 *   </BoostFrame>
 */
interface BoostFrameProps {
  boostLevel: BoostLevel;
  children: React.ReactNode;
  className?: string;
}

export const BoostFrame: React.FC<BoostFrameProps> = ({ boostLevel, children, className = "" }) => {
  const config = {
    1000: {
      borderGradient: "linear-gradient(to right, #fb923c, #f59e0b, #facc15)",
      glowColor: "0 0 20px rgba(251, 146, 60, 0.3)",
      shimmerBg: "linear-gradient(to right, transparent, rgba(251, 146, 60, 0.2), transparent)",
    },
    5000: {
      borderGradient: "linear-gradient(to right, #c084fc, #ec4899, #fb7185)",
      glowColor: "0 0 20px rgba(192, 132, 252, 0.3)",
      shimmerBg: "linear-gradient(to right, transparent, rgba(192, 132, 252, 0.2), transparent)",
    },
    10000: {
      borderGradient: "linear-gradient(to right, #22d3ee, #3b82f6, #6366f1)",
      glowColor: "0 0 20px rgba(34, 211, 238, 0.3)",
      shimmerBg: "linear-gradient(to right, transparent, rgba(34, 211, 238, 0.2), transparent)",
    },
  };

  const frameConfig = config[boostLevel];

  return (
    <div
      style={{
        position: "relative",
        padding: "2px",
        borderRadius: "10px",
        overflow: "hidden",
        background: frameConfig.borderGradient,
        boxShadow: `${frameConfig.glowColor}, 0 4px 6px rgba(0,0,0,0.1)`,
      }}
      className={className}
    >
      {/* Shimmer overlay */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: frameConfig.shimmerBg,
          pointerEvents: "none",
        }}
        className="animate-shimmer"
      />
      
      <div
        style={{
          position: "relative",
          background: "var(--surface)",
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        {children}
      </div>
    </div>
  );
};
